import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchSubjectsByClassroomId } from '../../services/subject/subject';
import type {
  ICreateSubjectRequestBody,
  ICreateSubjectResponseBody,
  ISubjectsByClassroomResponse,
  ISubjectsResponse,
  SubjectSearchParams,
  ICreateSubjectError,
  ISubject,
  IUpdateSubjectResponseBody,
  IAssignmentResponse,
  ICreateAssignmentRequest,
  ICreateAssignmentResponseBody,
  IDeleteAssignmentResponse,
  IAssignmentSearchParams,
  IDeleteAssignmentArgs,
  ISubjectAssignmentImpactCounts,
  IDeleteSubjectArgs,
  IDeleteSubjectResponse,
  ISubjectDeletionImpactCounts,
  ISubjectStatusResponse,
  ISetSubjectStatusArgs,
  ISubjectInactivateGatedResponse,
} from '../../interface/subjectModel';
import { IErrorResponse } from '../../interface/main.interface';
import { ITeacherSubjectRequestBody } from '../../interface/teacherModel';
import {
  createSubject,
  fetchSubject,
  getSubjectById,
  updateSubject,
  deleteSubject,
  setSubjectStatus,
  fetchAssignments,
  createAssignment,
  deleteAssignment,
} from '../../services/subject';
import axios, { AxiosError } from 'axios';
import { normalizeAxiosError } from '../../utility/axiosErrorHandler';
import { queryClient } from '../../services/queryClient';
import { fetchSubjectsQuery } from '../../hooks/reactQueryHooks';

// =====================================================
// FETCH SUBJECTS WITH REACT QUERY CACHING (24 HOURS)
// =====================================================

/**
 * Fetch subjects with 24-hour React Query cache
 * - Uses queryClient.fetchQuery for cache management
 * - Automatically updates Redux state via extraReducers
 * - Cache key: ['subjects', params]
 */
export const fetchSubjectsThunk = createAsyncThunk<
  ISubjectsResponse,
  SubjectSearchParams | void,
  { rejectValue: IErrorResponse }
>('subjects/fetch', async (arg, { rejectWithValue }) => {
  try {
    // Delegate cached fetch to shared helper (uses react-query's queryClient)
    const data = await fetchSubjectsQuery(arg as SubjectSearchParams | undefined);

    return data;
  } catch (error: any) {
    return rejectWithValue(normalizeAxiosError(error, 'Failed to fetch subjects.'));
  }
});

// =====================================================
// CREATE SUBJECT (INVALIDATES CACHE)
// =====================================================

/**
 * Create new subject and invalidate React Query cache
 * - Calls service layer to create subject
 * - Invalidates 'subjects' cache to trigger refetch
 * - Updates Redux state via extraReducers
 */
export const createSubjectProfile = createAsyncThunk<
  ICreateSubjectResponseBody,
  ICreateSubjectRequestBody,
  { rejectValue: IErrorResponse }
>('subject/create', async (body, { rejectWithValue }) => {
  try {
    const response = await createSubject(body);

    // Invalidate subjects cache to refetch latest data
    queryClient.invalidateQueries('subjects');

    return response;
  } catch (err: any) {
    return rejectWithValue(normalizeAxiosError(err, 'Failed to create subject.'));
  }
});

// =====================================================
// UPDATE SUBJECT (INVALIDATES CACHE)
// =====================================================

/**
 * Update existing subject and invalidate React Query cache
 * - Calls service layer to update subject
 * - Invalidates 'subjects' cache to trigger refetch
 * - Returns updated subject data
 */
export const updateSubjectThunk = createAsyncThunk<
  IUpdateSubjectResponseBody,
  { id: string; data: ICreateSubjectRequestBody },
  { rejectValue: IErrorResponse }
>('subject/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await updateSubject(id, data);

    // Invalidate subjects cache to refetch latest data
    queryClient.invalidateQueries('subjects');

    return response;
  } catch (err: any) {
    return rejectWithValue(normalizeAxiosError(err, 'Failed to update subject.'));
  }
});

// =====================================================
// DELETE SUBJECT (INVALIDATES CACHE)
// =====================================================

/**
 * Delete subject and invalidate React Query cache (two-step gated delete).
 * - First call (no confirm) runs the backend gate-check.
 * - On a soft-gate / hard-block 400 the axios interceptor rejects with the
 *   backend body, so we preserve the machine-readable `errorCode` and impact
 *   `data` for the gated-delete UI (mirrors deleteAssignmentThunk).
 * - Pass `confirmed: true` to cascade the deletion.
 */
export const deleteSubjectThunk = createAsyncThunk<
  IDeleteSubjectResponse,
  IDeleteSubjectArgs,
  { rejectValue: IErrorResponse & { errorCode?: string; data?: ISubjectDeletionImpactCounts } }
>('subject/delete', async ({ id, confirmed }, { rejectWithValue }) => {
  try {
    const response = await deleteSubject(id, confirmed);

    // Invalidate subjects cache to refetch latest data
    queryClient.invalidateQueries('subjects');

    return response;
  } catch (err: any) {
    return rejectWithValue({
      ...normalizeAxiosError(err, 'Failed to delete subject.'),
      errorCode: err?.errorCode,
      data: err?.data,
    });
  }
});

// =====================================================
// SET SUBJECT STATUS (ACTIVATE / INACTIVATE)
// =====================================================

/**
 * Activate/inactivate a master subject (PATCH /v2/subject/:id/status).
 *
 * The shared axios instance rejects with the response BODY directly (see the
 * interceptor in services/api.tsx), so on failure `err` is already the parsed
 * body. Forward the structured inactivation response (errorCode + impact
 * `data`) untouched so the screen can show the hard-block / soft-gate modal
 * instead of a plain error toast.
 */
export const setSubjectStatusThunk = createAsyncThunk<
  ISubjectStatusResponse,
  ISetSubjectStatusArgs,
  { rejectValue: ISubjectInactivateGatedResponse | IErrorResponse }
>('subject/set-status', async ({ id, status, confirmed }, { rejectWithValue }) => {
  try {
    const response = await setSubjectStatus(id, status, confirmed);
    queryClient.invalidateQueries('subjects');
    return response;
  } catch (err: any) {
    const gatedBody = err as Partial<ISubjectInactivateGatedResponse> & { message?: string };
    if (gatedBody?.errorCode) {
      return rejectWithValue(gatedBody as ISubjectInactivateGatedResponse);
    }
    return rejectWithValue(normalizeAxiosError(err, 'Failed to update subject status.'));
  }
});

// =====================================================
// GET SUBJECT BY ID (NO CACHING - FRESH DATA ALWAYS)
// =====================================================

/**
 * Get single subject by ID - no caching
 * - Always fetches fresh data from API
 * - Used for edit/view operations where latest data is needed
 */

/**
 * Get single subject by ID - no caching
 * - Always fetches fresh data from API
 * - Used for edit/view operations where latest data is needed
 */
export const getSubjectByIdThunk = createAsyncThunk<
  ISubject,
  string,
  { rejectValue: IErrorResponse }
>('subject/getById', async (id: string, { rejectWithValue }) => {
  try {
    // Service returns the API response payload (ISubjectsResponse).
    const response = await getSubjectById(id);

    // Normalize to a single ISubject for callers. Different API versions
    // may return the single subject in different shapes, for example:
    // - { status, message, data: { subject: {...} } }
    // - { status, message, data: { subjects: [{...}] } }
    // - { subject: {...} } or { subjects: [{...}] }
    const payload: any = response?.data ?? response;

    const subject: ISubject | undefined =
      payload?.subject ??
      payload?.subjects?.[0] ??
      payload?.data?.subject ??
      payload?.data?.subjects?.[0];

    if (!subject) {
      return rejectWithValue({ message: 'Subject not found' });
    }

    return subject as ISubject;
  } catch (err: any) {
    return rejectWithValue(normalizeAxiosError(err, 'Failed to fetch subject.'));
  }
});

/**
 * Fetch subjects by classroom ID
 * - Legacy operation - no caching applied
 */
export const getSubjectsByClassroom = createAsyncThunk<
  ISubjectsByClassroomResponse,
  string,
  { rejectValue: IErrorResponse }
>('subject/fetch-by-classroom', async (classroomId: string, { rejectWithValue }) => {
  try {
    const response = await fetchSubjectsByClassroomId(classroomId);
    return response;
  } catch (err: any) {
    return rejectWithValue({
      status: err?.status || 500,
      message: err?.message || 'Unknown error',
    });
  }
});

/**
 * Fetch subjects (alternative endpoint)
 * - Legacy operation - no caching applied
 */
export const FetchSubject = createAsyncThunk<
  ISubjectsResponse,
  ITeacherSubjectRequestBody | void,
  { rejectValue: ICreateSubjectError }
>('subject/fetchSubject', async (params, { rejectWithValue }) => {
  try {
    const response = await fetchSubject(params);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<ICreateSubjectError>;
      const errorResponse = error.response?.data;
      return rejectWithValue(errorResponse || { message: 'Unknown error' });
    } else {
      return rejectWithValue({ message: err.message });
    }
  }
});

// =====================================================
// ASSIGNMENT THUNKS WITH REACT QUERY CACHING (24 HOURS)
// =====================================================

/**
 * Fetch all subject assignments with optional pagination/filters
 * Backend endpoint: GET /v2/subject/assign
 * - Uses React Query caching with 24-hour TTL
 * - Cache key: ['subject-assignments', params]
 * - Invalidated on create/update/delete
 * @param params - Query parameters matching backend API (page, pageSize, searchtext, gradeId, curriculumId)
 */
export const fetchAssignmentsThunk = createAsyncThunk<
  IAssignmentResponse,
  IAssignmentSearchParams,
  { rejectValue: IErrorResponse }
>('assignments/fetch', async (params: IAssignmentSearchParams, { rejectWithValue }) => {
  try {
    const response = await fetchAssignments(params);
    return response;
  } catch (error: any) {
    return rejectWithValue(normalizeAxiosError(error, 'Failed to fetch assignments.'));
  }
});

/**
 * Create new subject assignment and invalidate cache
 * - Calls service layer to create assignment
 * - Invalidates 'subject-assignments' cache to trigger refetch
 * - Updates Redux state via extraReducers
 */
export const createAssignmentThunk = createAsyncThunk<
  ICreateAssignmentResponseBody,
  ICreateAssignmentRequest,
  { rejectValue: IErrorResponse }
>('assignments/create', async (assignmentData, { rejectWithValue }) => {
  try {
    const response = await createAssignment(assignmentData);

    return response;
  } catch (error: any) {
    return rejectWithValue(normalizeAxiosError(error, 'Failed to create assignment.'));
  }
});

/**
 * Delete subject assignment and invalidate cache
 * - Calls service layer to delete assignment
 * - Invalidates cache to trigger refetch
 */
export const deleteAssignmentThunk = createAsyncThunk<
  IDeleteAssignmentResponse,
  IDeleteAssignmentArgs,
  { rejectValue: IErrorResponse & { errorCode?: string; data?: ISubjectAssignmentImpactCounts } }
>('assignments/delete', async ({ id, confirmed }, { rejectWithValue }) => {
  try {
    const response = await deleteAssignment(id, confirmed);

    return response;
  } catch (error: any) {
    // The axios interceptor rejects with the backend body, so preserve the
    // machine-readable errorCode and impact `data` for the gated-delete UI.
    return rejectWithValue({
      ...normalizeAxiosError(error, 'Failed to delete assignment.'),
      errorCode: error?.errorCode,
      data: error?.data,
    });
  }
});

// =====================================================
// EXPORTED ACTIONS
// =====================================================

const subjectActions = {
  createSubjectProfile,
  fetchSubjectsThunk,
  updateSubjectThunk,
  deleteSubjectThunk,
  getSubjectByIdThunk,
  FetchSubject,
  getSubjectsByClassroom,
  // Assignment actions
  fetchAssignmentsThunk,
  createAssignmentThunk,
  deleteAssignmentThunk,
};

export default subjectActions;
